import fellowLogo from "@static/img/fellow-logo.svg";
import githubLogo from "@static/img/github.png";
import linearLogo from "@static/img/linear.png";
import salesforceLogo from "@static/img/salesforce.png";
import slackLogo from "@static/img/slack.png";
import teamsLogo from "@static/img/teams-64.png";
import youtrackLogo from "@static/img/youtrack.svg";

import type useLabel from "@apps/use-label/use-label";
import AppLink from "@components/link/link";

export const NATIVE_INTEGRATION_SLUGS = [
  "slack",
  "salesforce",
  "teams",
  "github",
  "fellow",
  "linear",
  "youtrack",
] as const;

export type NativeIntegrationSlug = (typeof NATIVE_INTEGRATION_SLUGS)[number];

export const getNativeIntegrationsConfig = (
  organization: any,
  label: ReturnType<typeof useLabel>,
): Record<
  string,
  {
    name: string;
    logo: string;
    summary: string;
    description: JSX.Element;
    categories: readonly string[];
  }
> => {
  const showNewSlackSettings = waffle.flag_is_active("new-slack-settings");

  return {
    slack: {
      name: "Slack",
      logo: slackLogo,
      categories: ["messaging"],
      summary:
        "Pull messages from public Slack channels to enrich AI summaries and reviews.",
      description: showNewSlackSettings ? (
        <p className="text-gray-600">
          Integrating Slack with Topicflow lets us extract key messages from
          public channels, improving context and highlighting important
          conversations in reviews and AI summaries, without needing to access
          DMs.
        </p>
      ) : (
        <>
          <ul className="list-disc ml-4 py-2">
            <li>Post meeting agendas and summaries to Slack</li>
            <li>
              <AppLink
                to={`/settings/organization/${organization.id}/notifications`}
                className="hover:underline text-blue-600"
              >
                Send notifications
              </AppLink>{" "}
              of{" "}
              {label("recognition", {
                pluralize: true,
                capitalize: false,
              })}{" "}
              and{" "}
              {label("goal", {
                pluralize: false,
                capitalize: false,
              })}{" "}
              check-ins to Slack
            </li>
          </ul>
        </>
      ),
    },
    salesforce: {
      name: "Salesforce",
      logo: salesforceLogo,
      categories: ["crm"],
      summary:
        "Preview Salesforce Opportunities in meeting notes and link Salesforce data to KPIs.",
      description: (
        <>
          <ul className="list-disc ml-4 py-2">
            <li>Preview Salesforce Opportunities in Topicflow meeting notes</li>
            <li>Link Salesforce data to Topicflow KPIs</li>
          </ul>
        </>
      ),
    },
    teams: {
      name: "Teams",
      logo: teamsLogo,
      categories: ["productivity"],
      summary:
        "Add the Topicflow bot to Microsoft Teams so employees can ask questions from Teams.",
      description: (
        <div>
          <p>
            Add the Topicflow bot to Microsoft Teams so employees can ask
            questions and get help from Topicflow directly in Teams.
          </p>
          <p className="mt-2">
            After connecting, see the{" "}
            <a
              href="https://docs.topicflow.com/settings/install-microsoft-teams"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline text-blue-600"
            >
              Microsoft Teams setup instructions
            </a>
            .
          </p>
        </div>
      ),
    },
    github: {
      name: "GitHub",
      logo: githubLogo,
      categories: ["ticketing"],
      summary:
        "Preview GitHub issues and pull requests in meeting notes and link them to KPIs.",
      description: (
        <>
          <ul className="list-disc ml-4 py-2">
            <li>
              Preview GitHub issues and pull requests in Topicflow meeting notes
            </li>
            <li>
              Search for GitHub issues and pull requests from the Explorer, and
              embed lists in meeting notes
            </li>
            <li>Link GitHub metrics to Topicflow KPIs</li>
          </ul>
          <p className="font-bold uppercase py-2 text-gray-400">Note:</p>
          <ul className="list-disc ml-4 text-gray-400">
            <li>
              Install the Topicflow GitHub app for the organization you want to
              link with Topicflow
            </li>
          </ul>
        </>
      ),
    },
    fellow: {
      name: "Fellow",
      logo: fellowLogo,
      categories: ["productivity"],
      summary:
        "Import Fellow transcripts into matching Topicflow meetings automatically.",
      description: (
        <>
          <ul className="list-disc ml-4 py-2">
            <li>Import Fellow transcripts into matching Topicflow meetings</li>
            <li>Keep transcript syncing enabled for this organization</li>
          </ul>
        </>
      ),
    },
    linear: {
      name: "Linear",
      logo: linearLogo,
      categories: ["ticketing"],
      summary:
        "Preview Linear issues and projects in meeting notes and embed them from the Explorer.",
      description: (
        <>
          <ul className="list-disc ml-4 py-2">
            <li>
              Preview Linear issues and projects in Topicflow meeting notes
            </li>
            <li>
              Search for Linear issues and projects from the Explorer, and embed
              lists in meeting notes
            </li>
          </ul>
        </>
      ),
    },
    youtrack: {
      name: "YouTrack",
      logo: youtrackLogo,
      categories: ["ticketing"],
      summary:
        "Sync YouTrack issues to work history and embed them in meeting notes.",
      description: (
        <>
          <ul className="list-disc ml-4 py-2">
            <li>Sync YouTrack issues to your work history</li>
            <li>Track issue activity and assignments</li>
            <li>
              Search for YouTrack issues from the Explorer, and embed lists in
              meeting notes
            </li>
          </ul>
        </>
      ),
    },
  };
};
